import { estimateClockOffset } from '@chromarble/game-core';
import { api } from './api';

/**
 * Server-time estimate. The client never decides when a match starts or ends; it only
 * displays the server's starts_at / ends_at corrected by this offset.
 */
class ServerClock {
  private offsetMs = 0;
  private bestRtt = Number.POSITIVE_INFINITY;
  private synced = false;

  now(): number {
    return Date.now() + this.offsetMs;
  }

  get isSynced(): boolean {
    return this.synced;
  }

  /** Feed a timestamp the server returned with any response (cheap continuous refinement). */
  observe(sentAt: number, serverTime: number, receivedAt: number): void {
    const { offsetMs, rttMs } = estimateClockOffset(sentAt, serverTime, receivedAt);
    // Prefer the sample with the smallest round trip; let older samples age out slowly.
    if (rttMs <= this.bestRtt * 1.2 || !this.synced) {
      this.offsetMs = offsetMs;
      this.bestRtt = Math.min(rttMs, this.bestRtt);
      this.synced = true;
    }
    this.bestRtt *= 1.02;
  }

  async sync(samples = 5): Promise<void> {
    for (let i = 0; i < samples; i++) {
      const sentAt = Date.now();
      try {
        const { serverTime } = await api.time();
        this.observe(sentAt, serverTime, Date.now());
      } catch {
        /* keep previous estimate */
      }
    }
  }
}

export const serverClock = new ServerClock();
